/**
 * Result of classifying an error for telemetry.
 * Contains a safe category name and optional metadata extracted from the error
 * without including raw messages that could contain PII.
 */ /**
 * Utilities for dealing with errors
 */ export class ErrorUtilities {
    /**
     * Set the redaction function to use for redacting error messages
     * @param redactionFunction Redact function to use
     */ static setRedactionFunction(redactionFunction) {
        this.redactionFunction = redactionFunction;
    }
    /**
     * Get the error message
     * @param error Error to extract message from
     * @param notRedact If true, do not redact the message
     */ static getMessage(error, notRedact) {
        let message = "";
        if (error) {
            if (error.message) {
                message = error.message;
            } else if (error.description) {
                message = error.description;
            } else if (typeof error === "object") {
                message = this.safeSerialize(error);
            } else if (error.toString) {
                message = error.toString();
            }
        }
        return notRedact ? message : this.redactMessage(message);
    }
    /**
     * Get the error stack trace
     * @param error Error to extract stack trace from
     * @param notRedact If true, do not redact the stack trace
     */ static getStackTrace(error, notRedact) {
        // get stack trace
        const stack = error?.stack ?? "";
        return notRedact ? stack : this.redactMessage(stack);
    }
    /**
     * Handle the unreachable case for switch statements by logging appropriately
     * @param value Value of the unreachable case
     * @param logPredicate Predicate to log the generated log
     */ static unreachableCase(value, logPredicate) {
        const logString = `Unreachable case found`;
        const error = new Error(logString);
        logPredicate(`${logString} [Case=${value}][StackTrace=${this.getStackTrace(error)}`);
    }
    /**
     * Classifies an error into a safe, non-PII category for telemetry and extracts
     * classification-specific metadata. Raw error messages are never included.
     *
     * Classifications:
     * - NullReference: TypeError with null/undefined access, extracts property name
     * - ChunkLoadError: webpack lazy-load failure, extracts chunk identifier
     * - ReferenceError: undefined variable, extracts variable name
     * - TypeError, RangeError, SyntaxError, URIError: category only
     * - Application errors (custom error.name): uses error.name as class
     * - UnknownError: fallback for unrecognized errors
     */ static classifyError(error) {
        const name = error.name ?? "";
        const message = error.message ?? "";
        // Webpack chunk/lazy-load failures — extract chunk identifier
        if (name === "ChunkLoadError" || message.startsWith("Loading chunk")) {
            const chunkMatch = message.match(/Loading chunk\s+(\S+)/);
            return {
                errorClass: "ChunkLoadError",
                metadata: {
                    chunkId: chunkMatch?.[1] ?? "unknown"
                }
            };
        }
        // Null/undefined reference — a TypeError with null/undefined access pattern
        if (error instanceof TypeError && /null|undefined/.test(message)) {
            // Extract the property name from patterns like "Cannot read properties of null (reading 'foo')"
            const propMatch = message.match(/reading '([^']+)'/);
            return {
                errorClass: "NullReference",
                metadata: propMatch ? {
                    property: propMatch[1]
                } : {}
            };
        }
        // Standard JS error types
        if (error instanceof TypeError) {
            return {
                errorClass: "TypeError",
                metadata: {}
            };
        }
        if (error instanceof RangeError) {
            return {
                errorClass: "RangeError",
                metadata: {}
            };
        }
        if (error instanceof SyntaxError) {
            return {
                errorClass: "SyntaxError",
                metadata: {}
            };
        }
        if (error instanceof ReferenceError) {
            // Extract the variable name from "X is not defined"
            const varMatch = message.match(/^(\S+) is not defined/);
            return {
                errorClass: "ReferenceError",
                metadata: varMatch ? {
                    variable: varMatch[1]
                } : {}
            };
        }
        if (error instanceof URIError) {
            return {
                errorClass: "URIError",
                metadata: {}
            };
        }
        // Application-specific error types (from @ms/uno-errors) — use error.name as class
        if (name !== "" && name !== "Error") {
            return {
                errorClass: name,
                metadata: {}
            };
        }
        return {
            errorClass: "UnknownError",
            metadata: {}
        };
    }
    /**
     * Extracts a safe component name from a React componentStack string.
     * The componentStack format is:
     *   "\n    at ComponentThatThrew (https://...)\n    at ParentComponent (https://...)\n    ..."
     * Returns the first component name with any URLs scrubbed, or "Unknown" if parsing fails.
     */ static extractComponentName(componentStack) {
        if (!componentStack) {
            return "Unknown";
        }
        // Match the first "at ComponentName" — \S+ stops at the space before "(url)"
        const match = componentStack.match(/at\s+(\S+)/);
        const name = match?.[1] ?? "Unknown";
        // Safety: strip anything that looks like a URL (http/https/file protocol or path-like strings)
        if (/^https?:|^file:|^\/|\\/.test(name)) {
            return "Unknown";
        }
        return name;
    }
    /**
     * This is a function you can call to safely serialize anything to JSON.
     * The built-in JSON.stringify() throws an exception for circular references and can't handle
     * many built-ins such as HTMLElements and the global window object.
     */ static safeSerialize(obj) {
        let str;
        try {
            const seenObjects = [];
            str = JSON.stringify(obj, (key, value)=>{
                if (value === window) {
                    return "[window]";
                } else if (value instanceof HTMLElement) {
                    return "[HTMLElement]";
                } else if (typeof value === "function") {
                    return "[function]";
                } else if (value instanceof Error) {
                    // JSON.stringify Error will return an empty object string {}, so we need to handle Error object separately
                    return value.message;
                } else if (typeof value === "object") {
                    if (value === null) {
                        return value;
                    } else if (seenObjects.indexOf(value) === -1) {
                        seenObjects.push(value);
                        return value;
                    } else {
                        return "[circular]";
                    }
                } else {
                    return value;
                }
            });
        } catch (err) {
            // Note: we make this be a JSON string, so that consumers
            // can always call JSON.parse.
            str = JSON.stringify("[object]");
        }
        return str;
    }
    /**
     * Helper to redact messages
     * @param message Message to redact
     */ static redactMessage(message) {
        if (this.redactionFunction) {
            return this.redactionFunction(message);
        }
        return message;
    }
}
